/**
 * useProperties - Hook personalizado para gestión de propiedades
 *
 * Centraliza la lógica común de propiedades:
 * - Fetching de datos desde Firestore con paginación
 * - Filtros funcionales (tipo, operación, precio, ubicación, estado)
 * - Ordenamiento
 * - Búsqueda por texto
 * - Sistema de favoritos
 */

import { useState, useCallback, useMemo } from 'react';
import {
  collection,
  getDocs,
  getDoc,
  doc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  QueryDocumentSnapshot,
  DocumentData,
  addDoc,
  deleteDoc,
  Timestamp,
  getCountFromServer
} from 'firebase/firestore';
import { db } from '../firebaseConfig';

// ============ INTERFACES ============

export interface Direccion {
  calle?: string;
  numero?: string;
  ciudad?: string;
  estado?: string;
  zona?: string;
  area?: string;
  unidad?: string;
  codigo_postal?: string;
}

export interface Coordenadas {
  lat: number;
  lng: number;
}

export interface Propiedad {
  id: string;
  // Identificación
  nombre?: string;
  titulo?: string;
  descripcion?: string;

  // Ubicación
  direccion?: string | Direccion;
  ciudad?: string;
  area?: string;
  comunidad?: string;
  zona?: string;
  estado_ubicacion?: string;
  coordenadas?: Coordenadas;
  latitud?: number;
  longitud?: number;

  // Precios
  precio?: number;
  precio_actual_usd?: number;
  precio_original_usd?: number;
  moneda?: string;

  // Tipo y operación
  tipo_operacion?: string;
  operacion?: string;
  tipo_propiedad?: string;
  tipo?: string;
  categoria?: string;

  // Características
  recamaras?: number;
  banos?: number;
  medios_banos?: number;
  tamano?: string;
  tamano_terreno?: number;
  tamano_construccion?: number;
  estacionamientos?: number;
  niveles?: number;
  antiguedad?: string;

  // Estado
  estado?: string;
  estatus_publicacion?: string;
  activo?: boolean;
  destacado?: boolean;

  // Multimedia
  imagen?: string;
  fotos?: string[];
  video_url?: string;
  tour_virtual?: string;

  // Relaciones
  inmobiliaria_doc_id?: string;
  asesor_doc_ids?: string[];
  proyecto_id?: string;

  // Amenidades
  amenidades?: string[];

  // Fechas
  fecha_creacion?: any;
  fecha_actualizacion?: any;
}

export interface PropertyFilters {
  busqueda?: string;
  tipo_operacion?: 'venta' | 'renta' | 'todos';
  tipo_propiedad?: string;
  ciudad?: string;
  zona?: string;
  precio_min?: number;
  precio_max?: number;
  recamaras_min?: number;
  banos_min?: number;
  tamano_min?: number;
  tamano_max?: number;
  estado?: 'Disponible' | 'Pendiente' | 'Vendida' | 'todos';
  destacado?: boolean;
  con_fotos?: boolean;
}

export interface PropertySort {
  campo: 'precio' | 'fecha_creacion' | 'recamaras' | 'tamano_construccion';
  direccion: 'asc' | 'desc';
}

export interface PaginationState {
  pagina: number;
  porPagina: number;
  total: number;
  totalPaginas: number;
  lastDoc: QueryDocumentSnapshot<DocumentData> | null;
}

export interface Favorito {
  id: string;
  userId: string;
  propertyId: string;
  fechaAgregado: Timestamp;
  notasUsuario?: string;
}

export interface UsePropertiesReturn {
  // Estado
  propiedades: Propiedad[];
  propiedad: Propiedad | null;
  loading: boolean;
  error: string | null;
  pagination: PaginationState;

  // Funciones de fetch
  fetchPropiedades: (filters?: PropertyFilters, sort?: PropertySort, resetPagination?: boolean) => Promise<Propiedad[]>;
  fetchPropiedadById: (id: string) => Promise<Propiedad | null>;
  fetchPropiedadesSimilares: (propiedad: Propiedad, limite?: number) => Promise<Propiedad[]>;
  fetchPropiedadesDestacadas: (limite?: number) => Promise<Propiedad[]>;
  fetchPropiedadesPorInmobiliaria: (inmobiliariaDocId: string) => Promise<Propiedad[]>;
  fetchPropiedadesPorAsesor: (asesorDocId: string) => Promise<Propiedad[]>;

  // Paginación
  cargarMas: () => Promise<void>;
  irAPagina: (pagina: number) => Promise<void>;
  cambiarPorPagina: (cantidad: number) => void;

  // Conteo
  contarPropiedades: (filters?: PropertyFilters) => Promise<number>;

  // Favoritos
  agregarFavorito: (userId: string, propertyId: string, notas?: string) => Promise<boolean>;
  eliminarFavorito: (favoritoId: string) => Promise<boolean>;
  esFavorito: (userId: string, propertyId: string) => Promise<boolean>;
  fetchFavoritos: (userId: string) => Promise<Propiedad[]>;

  // Utilidades
  filtrarPropiedades: (propiedades: Propiedad[], filters: PropertyFilters) => Propiedad[];
  ordenarPropiedades: (propiedades: Propiedad[], sort: PropertySort) => Propiedad[];
  formatearPrecio: (precio: number, moneda?: string) => string;
  formatearDireccion: (direccion: string | Direccion | undefined) => string;
  getCoordenadas: (propiedad: Propiedad) => Coordenadas | null;

  // Opciones para filtros
  tiposPropiedad: string[];
  ciudades: string[];
  zonas: string[];
}

// ============ HOOK ============

export function useProperties(): UsePropertiesReturn {
  const [propiedades, setPropiedades] = useState<Propiedad[]>([]);
  const [propiedad, setPropiedad] = useState<Propiedad | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<PaginationState>({
    pagina: 1,
    porPagina: 12,
    total: 0,
    totalPaginas: 0,
    lastDoc: null
  });

  // Filtros actuales (para paginación)
  const [currentFilters, setCurrentFilters] = useState<PropertyFilters>({});
  const [currentSort, setCurrentSort] = useState<PropertySort>({ campo: 'fecha_creacion', direccion: 'desc' });

  // ============ OPCIONES PARA FILTROS ============

  const tiposPropiedad = useMemo(() => [
    'departamento',
    'casa',
    'penthouse',
    'terreno',
    'local',
    'edificio',
    'negocio'
  ], []);

  const ciudades = useMemo(() => [
    'Nuevo Vallarta',
    'Puerto Vallarta',
    'Bucerías',
    'Punta de Mita',
    'Sayulita',
    'San Pancho',
    'La Cruz de Huanacaxtle',
    'Mezcales',
    'Flamingos',
    'Bahía de Banderas'
  ], []);

  const zonas = useMemo(() => [
    'Hotel Zone',
    'Nuevo Vallarta West',
    'Bucerias',
    'Marina',
    'La Cruz de Huanacaxtle',
    'Centro North',
    'Francisco Villa West',
    'Aramara',
    'Airport',
    'Centro South',
    'San Pancho',
    'Flamingos',
    'Francisco Villa East',
    'South Shore',
    'Mezcales',
    'Sayulita',
    'Nuevo Vallarta East'
  ], []);

  // ============ FUNCIONES DE FETCH ============

  /**
   * Fetch propiedades con filtros, ordenamiento y paginación
   */
  const fetchPropiedades = useCallback(async (
    filters: PropertyFilters = {},
    sort: PropertySort = { campo: 'fecha_creacion', direccion: 'desc' },
    resetPagination: boolean = true
  ): Promise<Propiedad[]> => {
    setLoading(true);
    setError(null);

    try {
      // Guardar filtros y sort actuales
      setCurrentFilters(filters);
      setCurrentSort(sort);

      // Base query - Filtrar propiedades activas (estado = 'A')
      // NOTA: En tu base de datos, las propiedades activas usan estado: 'A', no activo: true
      let constraints: any[] = [where('estado', '==', 'A')];

      // Aplicar filtros de Firestore (limitados por índices)
      if (filters.tipo_operacion && filters.tipo_operacion !== 'todos') {
        constraints.push(where('tipo_operacion', '==', filters.tipo_operacion));
      }

      if (filters.tipo_propiedad) {
        constraints.push(where('tipo_propiedad', '==', filters.tipo_propiedad));
      }

      if (filters.ciudad) {
        constraints.push(where('ciudad', '==', filters.ciudad));
      }

      // NOTA: El filtro de estado en PropertyFilters es para 'Disponible', 'Pendiente', etc.
      // No confundir con el campo 'estado' que usamos para 'A' (activo)
      // Si necesitas filtrar por estado de disponibilidad, usar otro campo

      if (filters.destacado) {
        constraints.push(where('destacado', '==', true));
      }

      // Paginación - Aumentamos el límite para compensar filtros en cliente
      const porPagina = pagination.porPagina * 2; // Traer más para filtrar en cliente
      constraints.push(limit(porPagina));

      const q = query(collection(db, 'propiedades'), ...constraints);
      const snap = await getDocs(q);

      let list = snap.docs.map(doc => ({ id: doc.id, ...doc.data() } as Propiedad));

      // Ordenamiento en cliente (evita necesidad de índices compuestos)
      list = ordenarPropiedades(list, sort);

      // Aplicar filtros adicionales en cliente (los que Firestore no puede hacer eficientemente)
      if (filters.precio_min !== undefined) {
        list = list.filter(p => (p.precio_actual_usd || p.precio || 0) >= filters.precio_min!);
      }

      if (filters.precio_max !== undefined) {
        list = list.filter(p => (p.precio_actual_usd || p.precio || 0) <= filters.precio_max!);
      }

      if (filters.recamaras_min !== undefined) {
        list = list.filter(p => (p.recamaras || 0) >= filters.recamaras_min!);
      }

      if (filters.banos_min !== undefined) {
        list = list.filter(p => (p.banos || 0) >= filters.banos_min!);
      }

      if (filters.busqueda) {
        const busqueda = filters.busqueda.toLowerCase();
        list = list.filter(p =>
          (p.nombre || '').toLowerCase().includes(busqueda) ||
          (p.titulo || '').toLowerCase().includes(busqueda) ||
          (p.descripcion || '').toLowerCase().includes(busqueda) ||
          (p.ciudad || '').toLowerCase().includes(busqueda) ||
          (p.zona || '').toLowerCase().includes(busqueda) ||
          (p.area || '').toLowerCase().includes(busqueda) ||
          (p.comunidad || '').toLowerCase().includes(busqueda) ||
          formatearDireccion(p.direccion).toLowerCase().includes(busqueda)
        );
      }

      if (filters.zona) {
        list = list.filter(p => (p.zona || '') === filters.zona);
      }

      if (filters.con_fotos) {
        list = list.filter(p => p.fotos && p.fotos.length > 0);
      }

      // Contar total
      const totalCount = await contarPropiedades(filters);

      // Actualizar estado
      if (resetPagination) {
        setPropiedades(list);
        setPagination({
          pagina: 1,
          porPagina,
          total: totalCount,
          totalPaginas: Math.ceil(totalCount / porPagina),
          lastDoc: snap.docs[snap.docs.length - 1] || null
        });
      } else {
        setPropiedades(prev => [...prev, ...list]);
        setPagination(prev => ({
          ...prev,
          pagina: prev.pagina + 1,
          lastDoc: snap.docs[snap.docs.length - 1] || null
        }));
      }

      return list;
    } catch (err: any) {
      console.error('Error fetching propiedades:', err);
      setError(err.message || 'Error al cargar propiedades');
      return [];
    } finally {
      setLoading(false);
    }
  }, [pagination.porPagina]);

  /**
   * Fetch propiedad por ID
   */
  const fetchPropiedadById = useCallback(async (id: string): Promise<Propiedad | null> => {
    setLoading(true);
    setError(null);

    try {
      const docRef = doc(db, 'propiedades', id);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = { id: docSnap.id, ...docSnap.data() } as Propiedad;
        setPropiedad(data);
        return data;
      }

      setError('Propiedad no encontrada');
      return null;
    } catch (err: any) {
      console.error('Error fetching propiedad:', err);
      setError(err.message || 'Error al cargar propiedad');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Fetch propiedades similares
   */
  const fetchPropiedadesSimilares = useCallback(async (
    prop: Propiedad,
    limite: number = 4
  ): Promise<Propiedad[]> => {
    try {
      const constraints: any[] = [
        where('estado', '==', 'A'),
        where('tipo_propiedad', '==', prop.tipo_propiedad || prop.tipo),
        limit(limite + 1) // +1 para excluir la propiedad actual
      ];

      if (prop.tipo_operacion || prop.operacion) {
        constraints.push(where('tipo_operacion', '==', prop.tipo_operacion || prop.operacion));
      }

      const q = query(collection(db, 'propiedades'), ...constraints);
      const snap = await getDocs(q);

      return snap.docs
        .map(doc => ({ id: doc.id, ...doc.data() } as Propiedad))
        .filter(p => p.id !== prop.id)
        .slice(0, limite);
    } catch (err) {
      console.error('Error fetching propiedades similares:', err);
      return [];
    }
  }, []);

  /**
   * Fetch propiedades destacadas
   */
  const fetchPropiedadesDestacadas = useCallback(async (limite: number = 8): Promise<Propiedad[]> => {
    try {
      const q = query(
        collection(db, 'propiedades'),
        where('estado', '==', 'A'),
        where('destacado', '==', true),
        orderBy('fecha_creacion', 'desc'),
        limit(limite)
      );

      const snap = await getDocs(q);
      return snap.docs.map(doc => ({ id: doc.id, ...doc.data() } as Propiedad));
    } catch (err) {
      console.error('Error fetching propiedades destacadas:', err);
      return [];
    }
  }, []);

  /**
   * Fetch propiedades por inmobiliaria
   */
  const fetchPropiedadesPorInmobiliaria = useCallback(async (
    inmobiliariaDocId: string
  ): Promise<Propiedad[]> => {
    try {
      const q = query(
        collection(db, 'propiedades'),
        where('estado', '==', 'A'),
        where('inmobiliaria_doc_id', '==', inmobiliariaDocId)
      );

      const snap = await getDocs(q);
      return snap.docs.map(doc => ({ id: doc.id, ...doc.data() } as Propiedad));
    } catch (err) {
      console.error('Error fetching propiedades por inmobiliaria:', err);
      return [];
    }
  }, []);

  /**
   * Fetch propiedades por asesor
   */
  const fetchPropiedadesPorAsesor = useCallback(async (
    asesorDocId: string
  ): Promise<Propiedad[]> => {
    try {
      const q = query(
        collection(db, 'propiedades'),
        where('estado', '==', 'A'),
        where('asesor_doc_ids', 'array-contains', asesorDocId)
      );

      const snap = await getDocs(q);
      return snap.docs.map(doc => ({ id: doc.id, ...doc.data() } as Propiedad));
    } catch (err) {
      console.error('Error fetching propiedades por asesor:', err);
      return [];
    }
  }, []);

  // ============ PAGINACIÓN ============

  const cargarMas = useCallback(async () => {
    if (loading || !pagination.lastDoc) return;
    await fetchPropiedades(currentFilters, currentSort, false);
  }, [loading, pagination.lastDoc, currentFilters, currentSort, fetchPropiedades]);

  const irAPagina = useCallback(async (pagina: number) => {
    // Para ir a una página específica, necesitamos recargar desde el inicio
    // y paginar hasta llegar a la página deseada
    // Por simplicidad, solo soportamos "siguiente" con lastDoc
    if (pagina === 1) {
      await fetchPropiedades(currentFilters, currentSort, true);
    }
  }, [currentFilters, currentSort, fetchPropiedades]);

  const cambiarPorPagina = useCallback((cantidad: number) => {
    setPagination(prev => ({
      ...prev,
      porPagina: cantidad,
      totalPaginas: Math.ceil(prev.total / cantidad)
    }));
  }, []);

  // ============ CONTEO ============

  const contarPropiedades = useCallback(async (filters: PropertyFilters = {}): Promise<number> => {
    try {
      let constraints: any[] = [where('estado', '==', 'A')];

      if (filters.tipo_operacion && filters.tipo_operacion !== 'todos') {
        constraints.push(where('tipo_operacion', '==', filters.tipo_operacion));
      }

      if (filters.tipo_propiedad) {
        constraints.push(where('tipo_propiedad', '==', filters.tipo_propiedad));
      }

      if (filters.ciudad) {
        constraints.push(where('ciudad', '==', filters.ciudad));
      }

      if (filters.estado && filters.estado !== 'todos') {
        constraints.push(where('estado', '==', filters.estado));
      }

      const q = query(collection(db, 'propiedades'), ...constraints);
      const snapshot = await getCountFromServer(q);
      return snapshot.data().count;
    } catch (err) {
      console.error('Error contando propiedades:', err);
      return 0;
    }
  }, []);

  // ============ FAVORITOS ============

  const agregarFavorito = useCallback(async (
    userId: string,
    propertyId: string,
    notas?: string
  ): Promise<boolean> => {
    try {
      // Verificar si ya existe
      const existe = await esFavorito(userId, propertyId);
      if (existe) return true;

      await addDoc(collection(db, 'favoritos'), {
        userId,
        propertyId,
        fechaAgregado: Timestamp.now(),
        notasUsuario: notas || ''
      });

      return true;
    } catch (err) {
      console.error('Error agregando favorito:', err);
      return false;
    }
  }, []);

  const eliminarFavorito = useCallback(async (favoritoId: string): Promise<boolean> => {
    try {
      await deleteDoc(doc(db, 'favoritos', favoritoId));
      return true;
    } catch (err) {
      console.error('Error eliminando favorito:', err);
      return false;
    }
  }, []);

  const esFavorito = useCallback(async (
    userId: string,
    propertyId: string
  ): Promise<boolean> => {
    try {
      const q = query(
        collection(db, 'favoritos'),
        where('userId', '==', userId),
        where('propertyId', '==', propertyId)
      );

      const snap = await getDocs(q);
      return !snap.empty;
    } catch (err) {
      console.error('Error verificando favorito:', err);
      return false;
    }
  }, []);

  const fetchFavoritos = useCallback(async (userId: string): Promise<Propiedad[]> => {
    try {
      const q = query(
        collection(db, 'favoritos'),
        where('userId', '==', userId),
        orderBy('fechaAgregado', 'desc')
      );

      const snap = await getDocs(q);
      const favoritos = snap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Favorito[];

      // Cargar las propiedades
      const propiedadesPromises = favoritos.map(fav => fetchPropiedadById(fav.propertyId));
      const propiedadesResults = await Promise.all(propiedadesPromises);

      return propiedadesResults.filter((p): p is Propiedad => p !== null);
    } catch (err) {
      console.error('Error fetching favoritos:', err);
      return [];
    }
  }, [fetchPropiedadById]);

  // ============ UTILIDADES ============

  const filtrarPropiedades = useCallback((
    props: Propiedad[],
    filters: PropertyFilters
  ): Propiedad[] => {
    let result = [...props];

    if (filters.busqueda) {
      const busqueda = filters.busqueda.toLowerCase();
      result = result.filter(p =>
        (p.nombre || '').toLowerCase().includes(busqueda) ||
        (p.titulo || '').toLowerCase().includes(busqueda) ||
        (p.descripcion || '').toLowerCase().includes(busqueda) ||
        (p.ciudad || '').toLowerCase().includes(busqueda) ||
        (p.zona || '').toLowerCase().includes(busqueda) ||
        (p.area || '').toLowerCase().includes(busqueda) ||
        (p.comunidad || '').toLowerCase().includes(busqueda)
      );
    }

    if (filters.tipo_operacion && filters.tipo_operacion !== 'todos') {
      result = result.filter(p =>
        (p.tipo_operacion || p.operacion) === filters.tipo_operacion
      );
    }

    if (filters.tipo_propiedad) {
      result = result.filter(p =>
        (p.tipo_propiedad || p.tipo) === filters.tipo_propiedad
      );
    }

    if (filters.ciudad) {
      result = result.filter(p => p.ciudad === filters.ciudad);
    }

    if (filters.precio_min !== undefined) {
      result = result.filter(p =>
        (p.precio_actual_usd || p.precio || 0) >= filters.precio_min!
      );
    }

    if (filters.precio_max !== undefined) {
      result = result.filter(p =>
        (p.precio_actual_usd || p.precio || 0) <= filters.precio_max!
      );
    }

    if (filters.recamaras_min !== undefined) {
      result = result.filter(p => (p.recamaras || 0) >= filters.recamaras_min!);
    }

    if (filters.banos_min !== undefined) {
      result = result.filter(p => (p.banos || 0) >= filters.banos_min!);
    }

    if (filters.estado && filters.estado !== 'todos') {
      result = result.filter(p => p.estado === filters.estado);
    }

    if (filters.con_fotos) {
      result = result.filter(p => p.fotos && p.fotos.length > 0);
    }

    return result;
  }, []);

  const ordenarPropiedades = useCallback((
    props: Propiedad[],
    sort: PropertySort
  ): Propiedad[] => {
    return [...props].sort((a, b) => {
      let valorA: any;
      let valorB: any;

      switch (sort.campo) {
        case 'precio':
          valorA = a.precio_actual_usd || a.precio || 0;
          valorB = b.precio_actual_usd || b.precio || 0;
          break;
        case 'fecha_creacion':
          valorA = a.fecha_creacion?.toDate?.() || new Date(0);
          valorB = b.fecha_creacion?.toDate?.() || new Date(0);
          break;
        case 'recamaras':
          valorA = a.recamaras || 0;
          valorB = b.recamaras || 0;
          break;
        case 'tamano_construccion':
          valorA = a.tamano_construccion || 0;
          valorB = b.tamano_construccion || 0;
          break;
        default:
          return 0;
      }

      if (sort.direccion === 'asc') {
        return valorA > valorB ? 1 : -1;
      } else {
        return valorA < valorB ? 1 : -1;
      }
    });
  }, []);

  const formatearPrecio = useCallback((precio: number, moneda: string = 'USD'): string => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: moneda,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(precio);
  }, []);

  const formatearDireccion = useCallback((direccion: string | Direccion | undefined): string => {
    if (!direccion) return '';

    if (typeof direccion === 'string') return direccion;

    const partes = [
      direccion.calle,
      direccion.numero,
      direccion.zona || direccion.area,
      direccion.ciudad,
      direccion.estado
    ].filter(Boolean);

    return partes.join(', ');
  }, []);

  const getCoordenadas = useCallback((prop: Propiedad): Coordenadas | null => {
    if (prop.coordenadas) {
      return prop.coordenadas;
    }

    if (prop.latitud && prop.longitud) {
      return { lat: prop.latitud, lng: prop.longitud };
    }

    return null;
  }, []);

  // ============ RETURN ============

  return {
    // Estado
    propiedades,
    propiedad,
    loading,
    error,
    pagination,

    // Funciones de fetch
    fetchPropiedades,
    fetchPropiedadById,
    fetchPropiedadesSimilares,
    fetchPropiedadesDestacadas,
    fetchPropiedadesPorInmobiliaria,
    fetchPropiedadesPorAsesor,

    // Paginación
    cargarMas,
    irAPagina,
    cambiarPorPagina,

    // Conteo
    contarPropiedades,

    // Favoritos
    agregarFavorito,
    eliminarFavorito,
    esFavorito,
    fetchFavoritos,

    // Utilidades
    filtrarPropiedades,
    ordenarPropiedades,
    formatearPrecio,
    formatearDireccion,
    getCoordenadas,

    // Opciones para filtros
    tiposPropiedad,
    ciudades,
    zonas
  };
}

export default useProperties;
